import type {ReactElement} from 'react';
import React, {useState, useEffect} from 'react';
import type {DragDirection, Range, ResizeEndEvent, Span} from '@Libraries/timeline/types';
import {getPageDuration, getPosterDuration} from '@Components/poster-editor/poster-editor-reducer';
import {DesignSeekItemBackgroundContainer} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/design-seek-item-background-container/design-seek-item-background-container';
import {BaseTimelineItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item';
import {
  updateDesignItemResizeData,
  updateDesignItemSelectedId,
  updateDesignItemSelectionState,
  updateDuplicateDuration,
  updateDuplicateMaxDuration,
  updateIsDesignItemResizing,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {POSTER_MAX_DURATION, POSTER_MIN_DURATION} from '@PosterWhiteboard/poster/poster.types';
import {addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {TimelineDesignSeekItemProps} from './timeline-design-seek-item.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {useGetPosterMinDuration} from '@Hooks/poster-editor/useGetPosterMinDuration';
import {AddPageGA4EventParams, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export function TimelineDesignSeekItem({isLastItem = false, ...props}: TimelineDesignSeekItemProps): ReactElement<any> | null {
  const pageDuration = useAppSelector((state) => getPageDuration(state, props.id));
  const minDuration = useGetPosterMinDuration();
  const posterDuration = useAppSelector(getPosterDuration);
  const dispatch = useAppDispatch();
  const isSelected = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignItemSelected;
  });
  const isResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isItemResizing;
  });
  const selectedDesignId = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.designItemSelectedId;
  });
  const designItemResizeData = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.designItemResizeData;
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [rangeOnResize, setRange] = useState(props.range);
  const isMultiPageEnabled = useAppSelector((state) => {
    return state.userPreferences.isMultiPageEnabled;
  });

  useEffect((): void => {
    if (!posterDuration) {
      return;
    }
    const newRange = rangeOnResize.end - rangeOnResize.start;
    const oldRange = props.range.end - props.range.start;
    dispatch(updateDuplicateDuration(posterDuration + newRange - oldRange));
  }, [rangeOnResize]);

  if (!posterDuration) {
    return null;
  }

  const onResizeEnd = async (event: ResizeEndEvent): Promise<void> => {
    const updatedRange = event.active.data.current.getSpanFromResizeEvent?.(event);
    if (!updatedRange) {
      return;
    }

    await updateOnResize(updatedRange, true);
    await window.posterEditor?.whiteboard?.audioClips.trimAudiosExceedingPageDuration();
    await window.posterEditor?.whiteboard?.getCurrentPage().items.trimAllTranscriptItemsExceedingPageDuration();
    dispatch(updateIsDesignItemResizing(false));
    dispatch(updateDuplicateMaxDuration(updatedRange.end - updatedRange.start));
    setTimeout((): void => {
      setIsUpdating(false);
    }, 300);
  };

  const updateOnResize = async (range: Range, undoable = false): Promise<void> => {
    const newDuration = range.end - range.start;
    const minDuration = window.posterEditor?.whiteboard?.getCurrentPage().items.getMaxAnimationTime() ?? POSTER_MIN_DURATION;

    const currentPageBeingEdited = window.posterEditor?.whiteboard?.pages.getPage(props.id);
    if (!currentPageBeingEdited) {
      return;
    }

    await currentPageBeingEdited.updateFromObject(
      {
        duration: Math.min(POSTER_MAX_DURATION, Math.max(minDuration, Number(newDuration.toFixed(2)))),
      },
      {
        undoable,
      }
    );
  };

  const getBackgroundImageContainer = (): ReactElement<any> => {
    return <DesignSeekItemBackgroundContainer pageId={props.id} />;
  };

  const getTimeInSeconds = (): number => {
    return pageDuration ?? 0;
  };

  const isDesignItemSelected = (): boolean => {
    if (selectedDesignId) {
      return !!isSelected && selectedDesignId === props.id;
    }
    return false;
  };

  const onClickDesignItem = async (): Promise<void> => {
    if (!isDesignItemSelected()) {
      window.posterEditor?.whiteboard?.audioClips.selectAudioItem('');
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.clearSelection();
      if (window.posterEditor?.whiteboard?.isPlaying()) {
        await window.posterEditor?.whiteboard?.pause();
      }
      window.posterEditor?.whiteboard?.setCurrentPage(props.id);
      dispatch(updateDesignItemSelectionState(true));
      dispatch(updateDesignItemSelectedId(props.id));
    }
  };

  const onResizeStart = (): void => {
    setRange(props.range);
    setIsUpdating(true);
    dispatch(updateDuplicateDuration(posterDuration));
    dispatch(updateIsDesignItemResizing(true));
  };

  const isResizePossible = (updatedRange: Span, direction: DragDirection): boolean => {
    if (direction === 'start' && updatedRange.start < 0) {
      return false;
    }

    const newDurationForPage = updatedRange.end - updatedRange.start;
    const difference = newDurationForPage - (props.range.end - props.range.start);
    return posterDuration + difference <= POSTER_MAX_DURATION;
  };

  const onResizeMove = async (updatedRange: Span, direction: DragDirection): Promise<void> => {
    if (!isResizePossible(updatedRange, direction)) {
      return;
    }

    const newDurationForPage = updatedRange.end - updatedRange.start;
    const difference = newDurationForPage - (props.range.end - props.range.start);

    dispatch(
      updateDesignItemResizeData({
        pageId: props.id,
        updatedRange,
        offset: difference,
      })
    );

    setRange(updatedRange);
  };

  const onAutoScroll = (increment: number, direction: DragDirection = 'end'): void => {
    if (direction === 'start') {
      setRange((oldRange) => {
        return {
          start: oldRange.start + increment,
          end: oldRange.end,
        };
      });
      return;
    }

    setRange((oldRange) => {
      return {
        start: oldRange.start,
        end: oldRange.end + increment,
      };
    });
  };

  const isBeingMovedBecauseOfResize = (): boolean => {
    if (!isResizing) {
      return false;
    }
    const pagesOrder = window.posterEditor?.whiteboard?.pages.pageOrder;
    if (!pagesOrder) {
      return false;
    }

    if (!designItemResizeData) {
      return false;
    }

    const indexOfPageBeingResized = pagesOrder.indexOf(designItemResizeData.pageId);
    const currentPageIndex = pagesOrder.indexOf(props.id);
    return currentPageIndex > indexOfPageBeingResized;
  };

  const getRange = (): Range => {
    if (designItemResizeData !== null && isBeingMovedBecauseOfResize()) {
      return {
        start: props.range.start + designItemResizeData.offset,
        end: props.range.end + designItemResizeData.offset,
      };
    }
    if (isUpdating) {
      return rangeOnResize;
    }

    return props.range;
  };

  const onAddNewPage = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.ADD_PAGE, {
      [GA4EventParamName.TYPE]: AddPageGA4EventParams.TIMELINE,
    });
    void addNewPage();
  };

  return (
    <BaseTimelineItem
      id={props.id}
      key={props.id}
      range={getRange()}
      duration={getTimeInSeconds()}
      onResizeStart={onResizeStart}
      onResizeMove={(updatedRangeValueOnResize, direction): void => {
        void onResizeMove(updatedRangeValueOnResize, direction);
      }}
      onResizeEnd={(e): void => {
        void onResizeEnd(e);
        dispatch(updateDesignItemResizeData(null));
      }}
      onAutoScroll={onAutoScroll}
      onClick={onClickDesignItem}
      isSelected={isDesignItemSelected()}
      minDuration={minDuration}
      maxDuration={POSTER_MAX_DURATION}
      isDesignItem
      showAddButton={isLastItem && isMultiPageEnabled}
      addItemIconSize={IconSize.SIZE_ICON_24}
      onClickAddButton={onAddNewPage}
      isMultiPageEnabled={isMultiPageEnabled}
    >
      {getBackgroundImageContainer()}
    </BaseTimelineItem>
  );
}
